import * as React from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { sortable } from '@patternfly/react-table';
import { Link } from 'react-router-dom';
import { useControllerContext } from '../../contexts/controller.context';
import { selectControllerById, wanSelectors } from '../../common/configuration/configuration.selectors';
import setControllerServers from './ControllerHelper';
import ControllerApi from '@app/api/controller-api/controller.actions';
import Site from '@app/api/site/site.actions';
import Wan from '@app/api/wan/wan.actions';
import GatewayApi from '@app/api/gateway-api/gateway.actions';
import Tunnel from '@app/api/tunnel/tunnel.actions';
import { Card, CardBody, PageSection, Spinner, Title, ToggleGroup, ToggleGroupItem, Tabs, Tab, TabTitleIcon, TabTitleText, Tooltip } from '@patternfly/react-core';
import ControllerIcon from '@app/bgimages/controllers-black.svg';
import SiteIcon from '@app/bgimages/sites-black.svg';
import { IconHeading } from '@app/components/IconHeading';
import { InfoSection } from '@app/components/InfoSection';
import { PatternflyTable } from '@app/components/PatternflyTable';
import { ArrowCircleDownIcon, PencilAltIcon, RegionsIcon, TopologyIcon, PficonNetworkRangeIcon } from '@patternfly/react-icons';
import { push } from 'connected-react-router';
import { NotFound } from '../not-found/not-found';
import { ErrorView } from '../error/error';
import '../../app.css';
import ThemeContext from '@app/providers/contexts/ThemeContext';
import Page from '@app/components/Page';
import { Button, CardHeader, Col, Row } from 'reactstrap';
import { Line } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Legend, ArcElement } from 'chart.js';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  getSortedRowModel,
  Column,
} from '@tanstack/react-table';
import { MdArrowDownward, MdArrowUpward, MdCircle } from 'react-icons/md';
import Loader from '@app/components/Loader';
import { useHistory } from 'react-router-dom';
import SiteApi from '@app/api/site-api/site.actions';
import MetricChart from '@app/components/MetricChart';
import { sanitizeErrorMessage } from '@app/lib/error-utils';
import DashboardApi from '@app/api/dashboard-api/dashboard.actions';
import { useMapCache } from '@app/hooks/useMapCache';
import DownloadImage from '@app/components/DownloadImage';

enum TabIndex {
  OSPF,
  BGP
}

type ColumnFiltersState = ColumnFilter[];
interface ColumnFilter {
  id: string;
  value: unknown;
}

interface ThemeContextType {
  theme: string;
  setTheme: (theme: string) => void;
}

interface Controller {
  name?: string;
  description?: string;
  ip_address?: string;
  address?: {
    address_1?: string;
    address_2?: string;
    city?: string;
    state?: string;
    country?: string;
    postal_code?: string;
    lat?: number;
    long?: number;
  };
  routingEnabled?: boolean;
  routingConfig?: {
    ospf_configuration?: string[];
    bgp_configuration?: string[];
    ospf_enabled?: boolean;
    bgp_enabled?: boolean;
  };
}

// Status color mapping to match site view
const statusColors: Record<number, string> = {
  0: "#000000", // Unknown - Black
  1: "#ff0000", // Down - Red
  2: "#FBC02D", // Warning - Yellow
  3: "#45B649", // Good - Green
  4: "#808080"  // Missing - Gray
};

// Status text mapping for tooltips
const statusTexts: Record<number, string> = {
  0: "Offline - Gateway has never been powered on",
  1: "Down - Gateway is down", 
  2: "Degraded - One or more links are not connected",
  3: "Online - Gateway is online and all links are connected",
  4: "Offline - Gateway has been offline for more than 2 weeks"
};

export const ControllerView: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id: contextId, isLoading, isSuccess, isFailed } = useControllerContext();
  const [activeTabIndex, setActiveTabIndex] = React.useState(TabIndex.OSPF);
  const [controller, setController] = React.useState<Controller | null>(null);
  const [sites, setSites] = React.useState([]);
  const [assigned, setAssigned] = React.useState([]);
  const [available, setAvailable] = React.useState([]);
  const [gateways, setGateways] = React.useState([]);
  const [lxdBridgeIp, setLxdBridgeIp] = React.useState('N/A');
  const [netmask, setNetmask] = React.useState('N/A');
  const [isLoadingController, setIsLoadingController] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const wanById = useSelector(wanSelectors.selectEntities);
  Chart.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Legend, ArcElement);
  const TitleMap = {
    [TabIndex.OSPF]: 'OSPF',
    [TabIndex.BGP]: 'BGP',
  };
  const [expandedRows, setExpandedRows] = React.useState({});
  const [controllerStatus, setControllerStatus] = React.useState(0);
  const [gatewayStatuses, setGatewayStatuses] = React.useState<Record<string, number>>({});
  const [gatewayStatusLoading, setGatewayStatusLoading] = React.useState<Record<string, boolean>>({});
  const [downloadStatus, setDownloadStatus] = React.useState('');
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [cancelDownloadRequestId, setCancelDownloadRequestId] = React.useState(0);

  const basePath = process.env.REACT_APP_BASE_PATH || '';

  const getIdFromUrl = () => {
    const url = window.location.pathname;
    const matches = url.match(/\/controller\/([^/]+)/);
    return matches ? matches[1] : null;
  };

  const id = contextId || getIdFromUrl();

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  React.useEffect(() => {
    (async function () {
      loadController();
      (document.getElementById('primary-app-container') as HTMLDivElement).scroll(0, 0);
    })();
  }, []);

  const loadController = async () => {
    if (!id) {
      console.error('Controller ID is undefined');
      setError('Controller ID is undefined');
      setIsLoadingController(false);
      return;
    }
    try {
      setIsLoadingController(true);
      setError(null);
      const controller = await ControllerApi.get(id);
      
      if (controller === undefined || controller === null) {
        console.error('Controller not found');
        history.push(`${basePath}/notfound`);
        return;
      }
      
      const gateways = await GatewayApi.getAll();
      const sites = await Site.getAll();
      const wans = await Wan.getAll();
      const tunnels = await Tunnel.getAll();

      controller.Servers = gateways.filter((gateway) => gateway.controller_id === id);

      for (let i = 0; i < controller.Servers.length; i++) {
        controller.Servers[i] = {
          ...controller.Servers[i],
          Tunnels: tunnels.filter((tunnel) => tunnel.serverId === controller.Servers[i].id),
        };
      }

      const controllerServers = setControllerServers(controller, sites, wans);
      const lxdBridgeIpAddr = controllerServers.assigned?.[0]?.ip_4_router ?? 'N/A';
      const controllerNetmask = controllerServers.assigned?.[0]?.ip_4_cidr ?? 'N/A';

      setLxdBridgeIp(lxdBridgeIpAddr);
      setNetmask(controllerNetmask);
      setSites(controllerServers.sites);
      setAssigned(controllerServers.assigned);
      setAvailable(controllerServers.available);
      setGateways([...controllerServers.assigned, ...controllerServers.available]);
      setController({
        name: controller.name,
        description: controller.description,
        address: controller.address || {},
        ip_address: controller.ip_address,
        routingEnabled: controller.dynamic_routing_enabled,
        routingConfig: controller.routing_configuration,
      });
    } catch (error: any) {
      console.error('Error loading controller data:', error);
      if (error.response && error.response.status === 404) {
        history.push(`${basePath}/notfound`);
      } else {
        setError(error.message || 'Failed to load controller data');
      }
    } finally {
      setIsLoadingController(false);
    }
  };


  const { controllers, sites: cachedSites, dashboardData } = useMapCache();
  const currentController = controllers.find(ctrl => ctrl.id === id);
  const centralizedControllerStatus = currentController?.status;

  // Get gateway statuses from centralized cache
  React.useEffect(() => {
    if (dashboardData?.partner?.gateways?.gateways) {
      const cachedGatewayStatuses = dashboardData.partner.gateways.gateways.reduce((acc, gateway) => {
        acc[gateway.id] = gateway.status;
        return acc;
      }, {} as Record<string, number>);
      
      setGatewayStatuses(cachedGatewayStatuses);
      
      const allGateways = Object.values(gateways).reduce<any[]>((acc, curr) => acc.concat(curr), []);
      setGatewayStatusLoading(prev => {
        const newLoading = { ...prev };
        allGateways.forEach(gateway => {
          newLoading[gateway.id] = false;
        });
        return newLoading;
      });
    }
  }, [dashboardData, gateways]);

  React.useEffect(() => {
    if (centralizedControllerStatus !== undefined) {
      setControllerStatus(centralizedControllerStatus);
    }
  }, [centralizedControllerStatus]);

  const mergeGatewaysAndSites = (gateways, sites) => {
    return gateways.map((gateway) => {
      const matchedSites = sites.filter((site) => {
        const serverIp = site.server?.ip_4_address;
        return serverIp && serverIp === gateway.ip_4_address;
      });

      return {
        ...gateway,
        sites: matchedSites,
      };
    });
  };

  const mergedGateways = mergeGatewaysAndSites(gateways, sites);

  const fullGateways = React.useMemo(() => mergedGateways, [mergedGateways]);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

  const columns = React.useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'Gateway ID',
        size: 400,
      },
      {
        accessorKey: 'ip_4_address',
        header: 'IP',
        cell: ({ row }: { row: any }) => row.original.ip_4_address,
        size: 200,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: any }) => (
          <>
            {gatewayStatusLoading[row.original.id] ? (
              <Spinner size="sm" />
            ) : (
              <Tooltip content={statusTexts[gatewayStatuses[row.original.id] || 0] || statusTexts[0]}>
                <MdCircle 
                  color={statusColors[gatewayStatuses[row.original.id] || 0]} 
                  size={14} 
                />
              </Tooltip>
            )}
          </>
        ),
        size: 100,
      },
    ],
    [gatewayStatuses, gatewayStatusLoading]
  );

  const table = useReactTable({
    data: fullGateways,
    columns,
    filterFns: {},
    state: {
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      pagination: {
        pageSize: 9999,
        pageIndex: 0,
      },
    },
    getPaginationRowModel: undefined,
  });

  const handleTabClick = (e, index) => {
    setActiveTabIndex(index);
  };

  const getSiteColumns = () => {
    return [
      { title: 'Address', transforms: [sortable] },
      { title: 'pWAN' },
      { title: 'Location' },
      { title: 'Company' },
      { title: 'Tunnel Gateway', transforms: [sortable] },
    ];
  };

  const getServerColumns = () => {
    return [{ title: 'IPv4 Address', transforms: [sortable] }, { title: 'ID' }];
  };

  const siteFromItem = React.useCallback((item) => {
    const wan = wanById[item.wanId];
    const wanLink = item.wanId ? <Link to={{ pathname: `${basePath}/wan/${item.wanId}` }}>{wan?.name}</Link> : '';
    return {
      cells: [
        { title: <Link to={{ pathname: `${basePath}/site/${item.id}` }}>{item.address1}</Link> },
        { title: wanLink },
        item.city,
        item.ClientCompany.name,
        item.server.ip_4_address,
      ],
    };
  }, []);

  const serverFromItem = (item) => {
    return {
      cells: [item.ip_4_address + item.ip_4_cidr, item.id],
    };
  };

  const getLocationInfo = React.useCallback(() => {
    return [
      { label: 'Address', value: controller?.address?.address_1 || 'N/A' },
      { label: 'City', value: controller?.address?.city || 'N/A' },
      { label: 'State/Province', value: controller?.address?.state || 'N/A' },
      { label: 'Country', value: controller?.address?.country || 'N/A' },
      { label: 'Postal Code', value: controller?.address?.postal_code || 'N/A' },
    ];
  }, [controller]);

  const getControllerInfo = React.useCallback(() => {
    const controllerIp =
      controller && controller.ip_address && controller.ip_address !== '' ? controller?.ip_address : 'N/A';

    return [
      { label: 'IP Address', value: controllerIp },
      // { label: "Controller Type", value: "Bridged/Routed" },
      { label: 'LXD Bridge IP', value: lxdBridgeIp },
      { label: 'Netmask', value: netmask },
    ];
  }, [controller, lxdBridgeIp]);

  const editController = React.useCallback(() => {
    dispatch(push(`${basePath}/controller/edit/${id}`));
  }, [dispatch, id]);

  const handleDownloadStatusChange = React.useCallback((update: { status: string; downloading: boolean }) => {
    setDownloadStatus(update?.status || '');
    setIsDownloading(!!update?.downloading);
  }, []);

  const handleDownloadStart = React.useCallback(() => {
    setDownloadStatus('Starting...');
    setIsDownloading(true);
  }, []);

  const handleCancelDownload = React.useCallback(() => {
    setCancelDownloadRequestId((current) => current + 1);
    setDownloadStatus('Cancelling...');
  }, []);

  const handleCancelComplete = React.useCallback(() => {
    setDownloadStatus('');
    setIsDownloading(false);
  }, []);

  if (isLoadingController) {
    return <Loader />;
  }

  if (error) {
    return <ErrorView title="Error loading controller" message={sanitizeErrorMessage(error)} />;
  }

  if (!controller) {
    return <NotFound />;
  }

  const ospfConfig = [];
  const bgpConfig = [];
  const ospf = controller?.routingConfig?.ospf_configuration;
  const bgp = controller?.routingConfig?.bgp_configuration;

  if (ospf) {
    for (let i = 0; i < ospf.length; i++) {
      ospfConfig.push(
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }} key={i}>
          {ospf[i]}
          <br />
        </div>
      );
    }
  }
  if (bgp) {
    for (let i = 0; i < bgp.length; i++) {
      bgpConfig.push(
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }} key={i}>
          {bgp[i]}
          <br />
        </div>
      );
    }
  }

  const { theme } = React.useContext(ThemeContext) as ThemeContextType;

  const cpuData = {
    labels: ['6:00', '7:00', '8:00', '9:00', '10:00', '11:00', '12:00'],
    datasets: [
      {
        label: controller?.name,
        data: [10, 9, 13, 11, 15, 12, 9],
        borderColor: '#45b649',
        borderWidth: 2,
        fill: false,
        tension: 0.4,
      },
    ],
  };

  const cpuOptions = (theme) => ({
    scales: {
      x: {
        ticks: {
          color: theme === 'dark' ? '#fff' : '#000',
        },
        grid: {
          color: theme === 'dark' ? '#444' : '#ddd',
        },
      },
      y: {
        min: 0,
        max: 20,
        ticks: {
          color: theme === 'dark' ? '#fff' : '#000',
          callback: function (value) {
            return value + '%';
          },
        },
        grid: {
          color: theme === 'dark' ? '#444' : '#ddd',
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top' as 'top' | 'left' | 'right' | 'bottom',
        labels: {
          font: {
            size: 14,
          },
          color: theme === 'dark' ? '#fff' : '#000',
        },
      },
      beforeDraw: (chart) => {
        const ctx = chart.ctx;
        ctx.save();
        ctx.fillStyle = theme === 'dark' ? '#000' : '#fff';
        ctx.fillRect(0, 0, chart.width, chart.height);
        ctx.restore();
      },
    },
  });

  const bandwidthData = {
    labels: ['6:00', '7:00', '8:00', '9:00', '10:00', '11:00', '12:00'],
    datasets: [
      {
        label: controller?.name,
        data: [1, 2, 1, 3, 3, 1, 3],
        borderColor: '#f85032',
        borderWidth: 2,
        fill: false,
        tension: 0.4,
      },
    ],
  };

  const bandwidthOptions = (theme: string) => ({
    scales: {
      x: {
        ticks: {
          color: theme === 'dark' ? '#fff' : '#000',
        },
        grid: {
          color: theme === 'dark' ? '#444' : '#ddd',
        },
      },
      y: {
        min: 0,
        max: 5,
        ticks: {
          color: theme === 'dark' ? '#fff' : '#000',
          callback: (value: any) => `${value}Mb/s`,
        },
        grid: {
          color: theme === 'dark' ? '#444' : '#ddd',
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top' as 'top' | 'left' | 'right' | 'bottom',
        labels: {
          font: {
            size: 14,
          },
          color: theme === 'dark' ? '#fff' : '#000',
        },
        beforeDraw: (chart) => {
          const ctx = chart.ctx;
          ctx.save();
          ctx.fillStyle = theme === 'dark' ? '#000' : '#fff';
          ctx.fillRect(0, 0, chart.width, chart.height);
          ctx.restore();
        },
      },
    },
  });

  const getStatus = (statusId) => {
    const statuses = [
      { status: 'Unknown', color: 'status-text-black' },    // Unknown status
      { status: 'Down', color: 'status-text-red' },         // Down status
      { status: 'Warning', color: 'status-text-yellow' },   // Warning status
      { status: 'Good', color: 'status-text-green' },       // Good status
      { status: 'Missing', color: 'status-text-gray' },     // Missing status
    ];
    return statuses[statusId];
  };

  // @ts-ignore
  return (
    <React.Fragment>
      {isLoading ? (
        <Loader />
      ) : (
        <Page
          tag="div"
          className={`cr-page px-3 pt-2 controllers-list ${theme}`}
          title=""
          breadcrumbs={[
            { name: 'Controllers', url: '/controllers', active: false },
            { name: controller?.name || '', active: true },
          ]}
          actionButton={null}
        >
          <Row className="mb-3">
            <Col lg={8} md={8} sm={12} xs={12}>
              <div className="controllers-card-wrapper d-flex position-relative h-100">
                <Title headingLevel="h1" size="lg" style={{ paddingRight: '70px' }}>
                  <strong>{controller?.name}</strong>
                </Title>
                <Row className="w-100">
                  <Col lg={10} md={10} sm={12} xs={12}>
                    <div className="w-100 mt-2 mb-2">
                      <strong>Status: </strong>&nbsp;<span className={`${getStatus(controllerStatus).color}`}>{getStatus(controllerStatus).status}</span>
                      <br />
                      <strong>Controller ID: </strong>&nbsp;{id}
                      <br />
                      <strong>Description: </strong>&nbsp;{controller?.description}
                    </div>
                  </Col>
                  <Col lg={2} md={2} sm={12} xs={12}>
                    <div className="d-flex flex-column">
                      <Button className="ethica-button-green w-100 mb-2" onClick={editController}>
                        Edit
                      </Button>
                    </div>
                  </Col>
                </Row>

                <Row className="w-100 m-auto">
                  <Col lg={6} md={6} sm={12} xs={12}>
                    <InfoSection keyValuePairs={getControllerInfo()} showHeading={false} />
                  </Col>
                  <Col lg={6} md={6} sm={12} xs={12}>
                    <InfoSection heading="Address" icon={<RegionsIcon />} keyValuePairs={getLocationInfo()} />
                  </Col>
                </Row>
              </div>
            </Col>
            <Col lg={4} md={4} sm={12} xs={12}>
              <div className="controllers-card-wrapper position-relative h-100 d-flex flex-column">
                <CardHeader className="p-2 fw-bold d-flex align-items-center w-100" style={{ minHeight: '40px', flex: '0 0 auto', backgroundColor: 'transparent', borderBottom: '1px solid #dee2e6', textTransform: 'none' }}>
                  Downloads
                </CardHeader>
                <CardBody className="p-3 w-100 d-flex flex-column" style={{ flex: '1 1 auto' }}>
                  <div className="py-2">
                    <div className="d-flex align-items-center mb-2">
                      <ArrowCircleDownIcon className="icon-medium" style={{ marginRight: '8px', color: isDownloading ? '#8a8d90' : '#6a9b1b' }} />
                      <span style={{ fontWeight: 600, color: isDownloading ? '#8a8d90' : '#2f3133' }}>Controller Package</span>
                    </div>
                    <div style={{ paddingLeft: '28px' }}>
                      <DownloadImage
                        imageType="controller"
                        imageId={id}
                        downloadTypeOverride="CAImage"
                        renderAsLink
                        linkLabel="amd64"
                        hideStatus
                        disabled={isDownloading}
                        isActiveDownload={isDownloading}
                        cancelRequestId={cancelDownloadRequestId}
                        onDownloadStart={handleDownloadStart}
                        onCancelComplete={handleCancelComplete}
                        onStatusChange={handleDownloadStatusChange}
                      />
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-between" style={{ minHeight: '24px', paddingTop: '12px', borderTop: '1px solid #dee2e6', color: '#4f5255', gap: '12px' }}>
                    <div>{downloadStatus ? `Controller Package (amd64): ${downloadStatus}` : '\u00A0'}</div>
                    {isDownloading ? (
                      <Button className="ethica-button-cancel" size="sm" onClick={handleCancelDownload}>
                        Cancel
                      </Button>
                    ) : null}
                  </div>
                </CardBody>
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={12} md={12} sm={12} xs={12}>
              <div className="controllers-card-wrapper d-flex position-relative">
                <Row className="w-100 m-auto">
                  <Col lg={12} md={12} sm={12} xs={12} className="h-100">
                    <h3 className="pt-4">Tunnel Gateways</h3>
                    {fullGateways.length === 0 ? (
                      <div className="w-100 text-center">No Data</div>
                    ) : (
                      <div style={{ minHeight: "250px", paddingBottom: "20px", maxHeight: "500px", overflowY: "auto" }}>
                        <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0', tableLayout: 'fixed' }}>
                          <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                              <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                  <th key={header.id} colSpan={header.colSpan} style={{ 
                                    paddingTop: '15px', 
                                    paddingBottom: '15px',
                                    width: header.column.id === 'id' ? '50%' : header.column.id === 'ip_4_address' ? '30%' : '20%',
                                    textAlign: 'left',
                                    paddingLeft: '15px'
                                  }}>
                                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                  </th>
                                ))}
                              </tr>
                            ))}
                          </thead>
                          <tbody>
                            {table.getCoreRowModel().rows.map((row) => (
                              <React.Fragment key={row.id}>
                                <tr style={{ height: '50px' }}>
                                  {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id} className="text-start" style={{ 
                                      paddingTop: '15px', 
                                      paddingBottom: '15px',
                                      paddingLeft: '15px',
                                      width: cell.column.id === 'id' ? '50%' : cell.column.id === 'ip_4_address' ? '30%' : '20%'
                                    }}>
                                      {Array.isArray(row.original.sites) &&
                                      row.original.sites.length > 0 &&
                                      cell.column.id === 'id' ? (
                                        <>
                                          <button
                                            onClick={() => toggleRow(row.id)}
                                            style={{
                                              background: 'none',
                                              border: 'none',
                                              cursor: 'pointer',
                                              transition: '.3s',
                                              marginRight: '5px'
                                            }}
                                          >
                                            {expandedRows[row.id] ? '▼' : '▶'}
                                          </button>
                                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </>
                                      ) : (
                                        flexRender(cell.column.columnDef.cell, cell.getContext())
                                      )}
                                    </td>
                                  ))}
                                </tr>

                                {expandedRows[row.id] && row.original.sites && (
                                  <tr>
                                    <td colSpan={3} style={{ padding: '0', borderTop: '1px solid #dee2e6' }}>
                                      <div style={{ margin: '0', padding: '10px 0', borderBottom: 'none', width: '100%' }}>
                                        <div style={{ 
                                          display: 'flex',
                                          width: '100%',
                                          paddingLeft: '34px',
                                          borderBottom: '1px solid #dee2e6',
                                          backgroundColor: '#f8f9fa'
                                        }}>
                                          <div style={{ 
                                            width: '20%',
                                            padding: '8px',
                                            fontSize: '12px',
                                            color: '#333',
                                            fontWeight: '600',
                                            textAlign: 'left'
                                          }}>
                                            Name
                                          </div>
                                          <div style={{ 
                                            width: '20%',
                                            padding: '8px',
                                            fontSize: '12px',
                                            color: '#333',
                                            fontWeight: '600',
                                            textAlign: 'left'
                                          }}>
                                            Assignment Type
                                          </div>
                                          <div style={{ 
                                            width: '20%',
                                            padding: '8px',
                                            fontSize: '12px',
                                            color: '#333',
                                            fontWeight: '600',
                                            textAlign: 'left'
                                          }}>
                                            Mode
                                          </div>
                                        </div>
                                        {Array.isArray(row.original.sites) &&
                                          row.original.sites.map((site, index) => (
                                            <div 
                                              key={index} 
                                              className="expanded-gateway-row"
                                              style={{ 
                                                display: 'flex',
                                                width: '100%',
                                                paddingLeft: '34px',
                                                borderTop: index === 0 ? 'none' : '1px solid #dee2e6'
                                              }}
                                            >
                                              <div style={{ 
                                                width: '20%',
                                                padding: '8px',
                                                fontSize: '12px',
                                                color: '#222222',
                                                textAlign: 'left'
                                              }}>
                                                {site.displayName || site.name || site.address1}
                                              </div>
                                              <div style={{ 
                                                width: '20%',
                                                padding: '8px',
                                                fontSize: '12px',
                                                color: '#222222',
                                                textAlign: 'left'
                                              }}>
                                                {site.wanId ? 'pWAN' : 'Site'}
                                              </div>
                                              <div style={{ 
                                                width: '20%',
                                                padding: '8px',
                                                fontSize: '12px',
                                                color: '#222222',
                                                textAlign: 'left'
                                              }}>
                                                {site.tunnelMode || 'N/A'}
                                              </div>
                                            </div>
                                          ))}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          <div className="controllers-card-wrapper d-flex position-relative">
            <Row className="w-100 m-auto">
              <Col lg={12} md={12} sm={12} xs={12}>
                <h3>Dynamic Routing</h3>
                <Card className="compact-card">
                  <CardBody>
                    {!controller?.routingEnabled ? (
                      <div>Dynamic Routing disabled</div>
                    ) : (
                      <div>
                        <Tabs activeKey={activeTabIndex} onSelect={handleTabClick} isBox>
                          <Tab
                            eventKey={TabIndex.OSPF}
                            title={
                              <React.Fragment>
                                <TabTitleIcon>
                                  <PficonNetworkRangeIcon className="icon-medium" />
                                </TabTitleIcon>{' '}
                                <TabTitleText>{TitleMap[TabIndex.OSPF]}</TabTitleText>
                              </React.Fragment>
                            }
                          >
                            {controller?.routingConfig?.ospf_enabled ? (
                              <div className="code-config">
                                <br />
                                {ospfConfig}
                              </div>
                            ) : (
                              <div>
                                <br />
                                OSPF disabled
                              </div>
                            )}
                          </Tab>
                          <Tab
                            eventKey={TabIndex.BGP}
                            title={
                              <React.Fragment>
                                <TabTitleIcon>
                                  <PficonNetworkRangeIcon className="icon-medium" />
                                </TabTitleIcon>{' '}
                                <TabTitleText>{TitleMap[TabIndex.BGP]}</TabTitleText>
                              </React.Fragment>
                            }
                          >
                            {controller?.routingConfig?.bgp_enabled ? (
                              <div className="code-config">
                                <br />
                                {bgpConfig}
                              </div>
                            ) : (
                              <div>
                                <br />
                                BGP disabled
                              </div>
                            )}
                          </Tab>
                        </Tabs>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </Page>
      )}
    </React.Fragment>
  );
};

function Filter({ column }: { column: Column<any, unknown> }) {
  const columnFilterValue = column.getFilterValue();
  const { filterVariant } = (column.columnDef.meta as { filterVariant?: string }) ?? {};

  return filterVariant === 'range' ? (
    <div>
      <div className="flex space-x-2">
        {/* See faceted column filters example for min max values functionality */}
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[0] ?? ''}
          onChange={(value) => column.setFilterValue((old: [number, number]) => [value, old?.[1]])}
          placeholder={`Min`}
          className="w-24 border shadow rounded"
        />
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[1] ?? ''}
          onChange={(value) => column.setFilterValue((old: [number, number]) => [old?.[0], value])}
          placeholder={`Max`}
          className="w-24 border shadow rounded"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : filterVariant === 'select' ? (
    <select onChange={(e) => column.setFilterValue(e.target.value)} value={columnFilterValue?.toString()}>
      {/* See faceted column filters example for dynamic select options */}
      <option value="">All</option>
      <option value="complicated">complicated</option>
      <option value="relationship">relationship</option>
      <option value="single">single</option>
    </select>
  ) : (
    <DebouncedInput
      className="form-control"
      onChange={(value) => column.setFilterValue(value)}
      placeholder={`Search...`}
      type="text"
      value={(columnFilterValue ?? '') as string}
    />
    // See faceted column filters example for datalist search suggestions
  );
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = React.useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  )
}
